import { useEffect, useState } from "react";
import { edit } from "../../asset";
import CustomInput from "../../components/CustomInput";
import SalaryStructure from "./SalaryStructure";
import { useNavigate } from "react-router-dom";
import { Staff } from "../../types/staff";
import { toast } from "sonner";
import payrollManagement from "../../api/payrollManagementApis";

export default function SalaryDetails({
  onNext,
  onBack,
  data,
}: {
  onNext: () => void;
  onBack: () => void;
  data: Staff;
}) {
  const [employeePF, setEmployeePF] = useState(false);
  const [proffTax, setProffTax] = useState(false);
  const [pfNumber, setPfNumber] = useState("");
  const [showSalaryStructure, setShowSalaryStructure] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const navigate = useNavigate();

  const handleUpdateClick = () => {
    navigate(`/staff/staff-profile/${data.staffId}`, {
      state: { showReminder: true, scrollTo: "financial" },
    });
  };

  const handleSave = async () => {
    if (!data?.staffId) return;

    if (employeePF && !pfNumber) {
      toast.error("Please enter PF number");
      return;
    }

    setLoading(true);

    const res = await payrollManagement.updateStatutoryComponent(data.staffId, {
      professionalTaxEnabled: proffTax,
      employeePfEnabled: employeePF,
      pfNumber: employeePF ? pfNumber : "",
    });

    setLoading(false);

    if (res?.status === 1) {
      setShowSalaryStructure(true);
      setIsEditing(false);
    }
  };

  useEffect(() => {
    if (!data?.staffId) return;

    const fetchSalary = async () => {
      const res = await payrollManagement.getSalaryStructure(data.staffId);

      if (res?.status === 1 && res.data) {
        const salary = res.data;

        setEmployeePF(salary.employeePfEnabled);
        setProffTax(salary.professionalTaxEnabled);
        setPfNumber(salary.pfNumber || "");

        // 👇 IMPORTANT: auto open next step if already exists
        if (
          salary.basicSalary !== null ||
          salary.pfAmount !== null ||
          salary.professionalTaxAmount !== null ||
          salary.employeePfEnabled ||
          salary.professionalTaxEnabled
        ) {
          setShowSalaryStructure(true);
        }
      }
    };

    fetchSalary();
  }, [data?.staffId]);

  return (
    <div className="grid gap-3">
      <div className="bg-white rounded-lg p-6 ">
        <div className="flex justify-between items-center">
          <h2 className="text-base text-neutral-500 font-medium mb-6">
            Bank Account Details
          </h2>
          <button
            onClick={handleUpdateClick}
            className="bg-warning-50 py-1 px-2 flex gap-2 items-center"
          >
            <img src={edit} alt="" />
            <span className=" text-warning-700 text-xs font-medium">Edit</span>
          </button>
        </div>
        <div className="grid grid-cols-2 gap-8">
          <div className="grid">
            <Info
              label="A/c Holder’s Name"
              value={data?.accountHolderName || "---"}
            />
            <Info label="A/c Number" value={data?.accountNumber || "---"} />
            <Info label="IFSC Code" value={data?.ifscCode || "---"} />
            <Info label="Bank Name" value={data?.bankName || "---"} />
            <Info label="Branch Name" value={data?.branchName || "---"} />
          </div>
          <div className="flex flex-col gap-1 text-[13px]">
            <span className="text-neutral-400 font-medium">Staff UAN </span>
            <span className="text-neutral-300">{data?.uanNumber || "---"}</span>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg p-6 flex flex-col">
        <div className="flex justify-between items-center">
          <div className="flex flex-col">
            <h2 className="text-base text-neutral-500 font-medium">
              Statutory Components
            </h2>
            <p className="text-xs text-neutral-400">
              Enable the necessary benefits and tax applicable for this
              employee.
            </p>
          </div>
          {showSalaryStructure && (
            <div
              onClick={() => setIsEditing(true)}
              className="bg-warning-50 cursor-pointer py-1 px-2 flex gap-2 items-center"
            >
              <img src={edit} alt="" />
              <button className="text-warning-700 text-xs font-medium">
                Edit
              </button>
            </div>
          )}
        </div>
        <div className="flex flex-col gap-2 mt-4">
          <label className="flex items-center gap-2 text-sm text-neutral-500">
            <input
              type="checkbox"
              checked={proffTax}
              disabled={showSalaryStructure && !isEditing}
              onChange={(e) => setProffTax(e.target.checked)}
              className="w-[13px] h-[13px]"
            />
            Professional Tax
          </label>
          <div className="flex gap-2">
            <label className="flex items-center text-nowrap gap-2 text-sm text-neutral-500">
              <input
                type="checkbox"
                checked={employeePF}
                disabled={showSalaryStructure && !isEditing}
                onChange={(e) => setEmployeePF(e.target.checked)}
                className="w-[13px] h-[13px]"
              />
              Employee PF
            </label>
            {employeePF && (
              <CustomInput
                name="pfNumber"
                placeholder="Re- Enter Staff UAN"
                value={pfNumber}
                disabled={showSalaryStructure && !isEditing}
                onChange={(e) => setPfNumber(e.target.value)}
                inputClassName="w-[30%] py-[5px]"
              />
            )}
          </div>
        </div>
        {(isEditing || !showSalaryStructure) && (employeePF || proffTax) && (
          <div className="flex justify-end gap-2">
            <button
              onClick={onBack}
              className="px-6 py-[6px] border border-neutral-400 text-neutral-400 rounded-lg text-[13px] font-medium"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={loading}
              className="px-4 py-[6px] bg-success-700 text-white rounded-lg text-[13px]"
            >
              {loading ? "Saving..." : "Save and Continue"}
            </button>
          </div>
        )}
      </div>
      <SalaryStructure
        staffId={data?.staffId}
        showSalaryStructure={showSalaryStructure}
        setShowSalaryStructure={setShowSalaryStructure}
        onNext={onNext}
        employeePF={employeePF} 
        professionalTax={proffTax}
      />
    </div>
  );
}

function Info({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-1 text-[13px]">
      <span className="text-neutral-400 font-medium">{label} - </span>
      <span className="text-neutral-300">{value}</span>
    </div>
  );
}
